import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Input, Result, Typography } from 'antd';
import { createEndpoints } from '../api/endpoints.js';
import { createHttp } from '../api/http.js';
import logoUrl from '../assets/lynear-wealth-logo.svg';
import { PageLoader } from '../components/Loader.jsx';
import { AccessProvider } from './AccessContext.jsx';
import { ACTIVITY_EVENTS, createIdleMonitor } from './idle.js';
import IdleWarning from './IdleWarning.jsx';
import MfaSetup from './MfaSetup.jsx';
import PasswordField from './PasswordField.jsx';
import { cooldownLeft } from './resetCooldown.js';
import { createSession } from './session.js';

/**
 * Signing in to the Console (docs/auth/console-identity-and-access.md sections
 * 5 and 6). Every step is a call to the Console API, which speaks to the user
 * pool on the Console's behalf; the browser never calls Cognito.
 *
 *   RESUMING       a reload: the session cookie may still name a live session
 *   SIGN_IN        email and password
 *   FORGOT_EMAIL   the address a reset code is sent to
 *   FORGOT_CONFIRM the code from that email, and a new password
 *   NEW_PASSWORD   the invitation's first sign-in: choose a password (the policy's checklist)
 *   MFA_SETUP      scan the QR code, enter the first code - then sign in again
 *   MFA            the authenticator code
 *   CHECKING     /api/me: who the Core says the person is, and what they may see
 *   READY        the Console
 *   DENIED       signed in, but /api/me refused
 *
 * Signed in, the 10-minute idle rule and the 1-hour limit apply: a warning a
 * minute before the idle sign-out, and a message saying why after either. The
 * server enforces both whatever this page does.
 */
const PHASE = Object.freeze({
    RESUMING: 'RESUMING',
    SIGN_IN: 'SIGN_IN',
    FORGOT_EMAIL: 'FORGOT_EMAIL',
    FORGOT_CONFIRM: 'FORGOT_CONFIRM',
    NEW_PASSWORD: 'NEW_PASSWORD',
    MFA_SETUP: 'MFA_SETUP',
    MFA: 'MFA',
    CHECKING: 'CHECKING',
    READY: 'READY',
    DENIED: 'DENIED'
});

/** Codes that mean "this sign-in cannot go on; start it again". */
const RESTART = new Set(['FLOW_INVALID', 'FLOW_EXPIRED', 'ACCOUNT_NOT_ACTIVE', 'INVITATION_EXPIRED']);

const Brand = ({ title, sub }) => (
    <div className="auth-brand">
        <div className="auth-brand-lockup" aria-label="Lynear Wealth">
            <img className="auth-brand-logo" src={logoUrl} alt="" />
            <div className="auth-brand-wordmark">
                <span>Lynear</span>
                <span>Wealth</span>
            </div>
        </div>
        <div className="auth-brand-heading">
            <h1 className="auth-brand-title">{title}</h1>
            {sub && <p className="auth-brand-sub">{sub}</p>}
        </div>
    </div>
);

const AuthGate = ({ config, children }) => {
    const [phase, setPhase] = useState(PHASE.RESUMING);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [email, setEmail] = useState('');
    const [flow, setFlow] = useState(null);
    const [setup, setSetup] = useState(null);
    const [principal, setPrincipal] = useState(null);
    const [denial, setDenial] = useState(null);
    const [warning, setWarning] = useState(false);
    const [mfaKey, setMfaKey] = useState(0);
    const [mfaCode, setMfaCode] = useState('');
    const [codeSentAt, setCodeSentAt] = useState(null);
    const [clock, setClock] = useState(() => Date.now());
    const [signInForm] = Form.useForm();

    // The session and the API are built once; each needs the other. They are
    // tied together INSIDE one factory call and nothing outside it is written:
    // StrictMode runs a useMemo factory twice in development and keeps the
    // first result, so a factory that also stored its session in a ref left
    // the API reading a second, empty session - the sign-in succeeded and the
    // Console then waited for ever on "Opening Fund Operations".
    const endedRef = useRef(() => {});
    // The sixth digit and the Submit button can both ask to send in the same
    // moment (Enter on the last box). One request is enough.
    const mfaInFlight = useRef(false);
    const { api, session } = useMemo(() => {
        let s = null;
        const http = createHttp({
            apiBase: config.apiBase,
            accessToken: () => s.accessToken(),
            refresh: () => s.refresh(),
            onSessionEnded: (code, message) => s.end(code, message)
        });
        const endpoints = createEndpoints(http);
        s = createSession({
            refreshCall: endpoints.auth.refresh,
            onEnded: (code, message) => endedRef.current(code, message)
        });
        return { api: endpoints, session: s };
    }, [config.apiBase]);

    const toSignIn = useCallback((message = null, kind = 'info') => {
        setPrincipal(null);
        setFlow(null);
        setSetup(null);
        setMfaCode('');
        setWarning(false);
        setError(kind === 'error' ? message : null);
        setNotice(kind === 'info' ? message : null);
        setPhase(PHASE.SIGN_IN);
    }, []);

    // The server ended the session (idle, the hour, an administrator): say why.
    endedRef.current = (code, message) => toSignIn(message, code === 'SESSION_UNKNOWN' ? 'info' : 'error');

    const loadPrincipal = useCallback(async () => {
        setPhase(PHASE.CHECKING);
        try {
            const me = await api.me();
            setPrincipal(me);
            setPhase(PHASE.READY);
        } catch (e) {
            if (session.isSignedIn()) {
                setDenial({ text: e.message, retryable: e.status === 0 || e.status >= 500 });
                setPhase(PHASE.DENIED);
            } else {
                // The session ended while loading: never leave the page on the loader.
                toSignIn(e.message, 'error');
            }
        }
    }, [api, session, toSignIn]);

    /**
     * Live screens (docs/live-sync/implementation-plan.md 4.6): a session hint
     * says this person's access changed - suspended, reset, roles changed - so
     * re-read who they are WITHOUT the page loader, which would unmount every
     * open screen (and anything half-typed in it) for what is usually a
     * change of roles. A new principal re-draws what they may see in place.
     * A session the server has ended signs out with the reason, as any API
     * call that finds it ended does; any other failure keeps the page as it
     * is - the person's next action meets the same answer.
     */
    const refreshPrincipal = useCallback(async () => {
        try {
            setPrincipal(await api.me());
        } catch (e) {
            if (!session.isSignedIn()) toSignIn(e.message, 'error');
        }
    }, [api, session, toSignIn]);

    // A reload: resume the session the cookie names, if it is still live.
    useEffect(() => {
        let cancelled = false;
        session.resume().then((live) => {
            if (cancelled) return;
            if (live) loadPrincipal();
            else setPhase(PHASE.SIGN_IN);
        });
        return () => {
            cancelled = true;
        };
    }, [session, loadPrincipal]);

    // Lock the host page. The auth screen itself scrolls when a step is taller than the viewport.
    useEffect(() => {
        if (phase === PHASE.READY) return undefined;
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById('lfoc-root');
        const previous = [html, body, root].map((node) => ({
            node,
            overflow: node?.style.overflow,
            height: node?.style.height,
            width: node?.style.width
        }));
        [html, body, root].forEach((node) => {
            if (!node) return;
            node.style.overflow = 'hidden';
            node.style.height = '100%';
            node.style.width = '100%';
        });
        return () => {
            previous.forEach(({ node, overflow, height, width }) => {
                if (!node) return;
                node.style.overflow = overflow;
                node.style.height = height;
                node.style.width = width;
            });
        };
    }, [phase]);

    const signOut = useCallback(async (message = null) => {
        try {
            await api.auth.signOut();
        } catch {
            // The server session also ends by itself; the page forgets it either way.
        }
        session.end('SIGNED_OUT', '');
        toSignIn(typeof message === 'string' ? message : null);
    }, [api, session, toSignIn]);

    // Signed in: the idle monitor, the heartbeat, and the hour's limit.
    const monitorRef = useRef(null);
    useEffect(() => {
        if (phase !== PHASE.READY) return undefined;
        const monitor = createIdleMonitor({
            onWarn: () => setWarning(true),
            onActive: () => setWarning(false),
            onIdle: () => signOut('You were signed out after 10 minutes without activity.'),
            heartbeat: () => api.auth.activity()
        });
        monitorRef.current = monitor;
        const onActivity = () => monitor.activity();
        ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, { passive: true }));
        const limit = Date.parse(session.limits()?.absoluteExpiresAt ?? '') - Date.now();
        const hour = Number.isFinite(limit)
            ? setTimeout(() => signOut('Your session reached its one-hour limit. Please sign in again.'), Math.max(0, limit))
            : null;
        return () => {
            monitor.stop();
            ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity));
            if (hour) clearTimeout(hour);
        };
    }, [phase, api, session, signOut]);

    /** Run one step; a refusal that ends the sign-in goes back to the start. */
    const step = async (work) => {
        setBusy(true);
        setError(null);
        try {
            await work();
        } catch (e) {
            if (RESTART.has(e.code)) toSignIn(e.message, 'error');
            else setError(e.message);
        } finally {
            setBusy(false);
        }
    };

    /** Where Cognito's answer leads. */
    const follow = async (answer) => {
        if (answer.next === 'NEW_PASSWORD') {
            setFlow(answer.flow);
            setPhase(PHASE.NEW_PASSWORD);
        } else if (answer.next === 'MFA_SETUP') {
            const started = await api.auth.mfaSetupStart(answer.flow);
            setSetup(started);
            setPhase(PHASE.MFA_SETUP);
        } else if (answer.next === 'MFA') {
            setFlow(answer.flow);
            setMfaCode('');
            setMfaKey((k) => k + 1);
            setPhase(PHASE.MFA);
        }
    };

    // While a code was just sent, tick once a second so the resend button counts down.
    useEffect(() => {
        if (phase !== PHASE.FORGOT_CONFIRM || cooldownLeft(codeSentAt, Date.now()) === 0) return undefined;
        const timer = setInterval(() => setClock(Date.now()), 1000);
        return () => clearInterval(timer);
    }, [phase, codeSentAt]);
    const resendWait = phase === PHASE.FORGOT_CONFIRM ? cooldownLeft(codeSentAt, clock) : 0;

    const submitForgot = ({ email: address }) => step(async () => {
        const normalised = address.trim().toLowerCase();
        setEmail(normalised);
        signInForm.setFieldsValue({ email: normalised });
        setNotice(null);
        await api.auth.forgotPassword(normalised);
        setCodeSentAt(Date.now());
        setClock(Date.now());
        setNotice('If that address is an active Fund Operations account, a code is on its way. It expires in about an hour. If you have not finished your invitation, ask an administrator to send it again.');
        setPhase(PHASE.FORGOT_CONFIRM);
    });

    const resendCode = () => step(async () => {
        setNotice(null);
        await api.auth.forgotPassword(email);
        setCodeSentAt(Date.now());
        setClock(Date.now());
        setNotice('If that address is an active account, a new code is on its way.');
    });

    const submitReset = ({ code, newPassword }) => step(async () => {
        await api.auth.confirmForgotPassword(email, code, newPassword);
        toSignIn('Your password is changed. Sign in with it, then enter a code from your authenticator app.');
    });

    const submitSignIn = ({ email: address, password }) => step(async () => {
        setNotice(null);
        setEmail(address.trim().toLowerCase());
        await follow(await api.auth.signIn(address, password));
    });

    const submitNewPassword = ({ newPassword }) => step(async () => {
        await follow(await api.auth.newPassword(flow, newPassword));
    });

    const submitSetupCode = (code) => {
        if (!/^\d{6}$/.test(code ?? '')) return;
        step(async () => {
            await api.auth.mfaSetupVerify(setup.flow, code);
            // The sign-in form comes back with the email filled in (initialValues).
            toSignIn('Your authenticator is set up. Sign in with your new password and a code from the app.');
        });
    };

    const submitMfa = (code) => {
        // The sixth digit submits on its own; Enter or Submit in the same moment must not send a second request.
        if (mfaInFlight.current || !/^\d{6}$/.test(code ?? '')) return;
        mfaInFlight.current = true;
        step(async () => {
            try {
                session.begin(await api.auth.mfa(flow, code));
            } catch (e) {
                setMfaCode('');
                setMfaKey((k) => k + 1);
                throw e;
            }
            await loadPrincipal();
        }).finally(() => {
            mfaInFlight.current = false;
        });
    };

    if (phase === PHASE.READY) {
        return (
            <AccessProvider principal={principal} api={api} signOut={signOut} reloadPrincipal={loadPrincipal} refreshPrincipal={refreshPrincipal}>
                {children}
                <IdleWarning
                    open={warning}
                    secondsLeft={() => monitorRef.current?.secondsLeft() ?? 0}
                    onStay={() => monitorRef.current?.activity()}
                    onSignOut={() => signOut()}
                />
            </AccessProvider>
        );
    }

    if (phase === PHASE.RESUMING || phase === PHASE.CHECKING) {
        return (
            <PageLoader>
                <p className="auth-hint">{phase === PHASE.CHECKING ? 'Opening Fund Operations…' : 'Checking your session…'}</p>
            </PageLoader>
        );
    }

    if (phase === PHASE.DENIED) {
        return (
            <div className="auth-screen">
                <Result
                    status={denial?.retryable ? 'error' : '403'}
                    title={denial?.retryable ? 'Fund Operations could not load' : 'No Fund Operations access'}
                    subTitle={denial?.text}
                    extra={[
                        denial?.retryable && <Button key="retry" type="primary" onClick={loadPrincipal}>Retry</Button>,
                        <Button key="out" onClick={() => signOut()}>Sign out</Button>
                    ].filter(Boolean)}
                />
            </div>
        );
    }

    const cancel = () => toSignIn(null);

    return (
        <div className="auth-screen">
            <div className={`auth-card${phase === PHASE.MFA ? ' auth-card--otp' : ''}`}>
                {phase === PHASE.SIGN_IN && (
                    <>
                        <Brand title="Sign In" />
                        {notice && <Alert type="success" showIcon message={notice} className="auth-alert" />}
                        {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                        <Form
                            className="auth-form"
                            form={signInForm}
                            layout="vertical"
                            requiredMark={(label, { required }) => (
                                <>
                                    {label}
                                    {required && <span className="auth-required-mark">*</span>}
                                </>
                            )}
                            onFinish={submitSignIn}
                            disabled={busy}
                            initialValues={{ email }}
                        >
                            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter your email address' }]}>
                                <Input placeholder="Email" autoComplete="username" autoFocus={!email} />
                            </Form.Item>
                            <Form.Item name="password" label="Password" rules={[{ required: true, message: 'Enter your password' }]}>
                                <Input.Password placeholder="Password" autoComplete="current-password" autoFocus={Boolean(email)} />
                            </Form.Item>
                            <Button className="auth-submit" type="primary" htmlType="submit" block loading={busy}>
                                Sign In
                            </Button>
                        </Form>
                    </>
                )}

                {phase === PHASE.FORGOT_EMAIL && (
                    <>
                        <Brand title="Forgot password" sub="We email a code when this address is an active account." />
                        {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                        <Form className="auth-form" layout="vertical" requiredMark={false} onFinish={submitForgot} disabled={busy} initialValues={{ email }}>
                            <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email', message: 'Enter your email address' }]}>
                                <Input placeholder="Email" autoComplete="username" autoFocus />
                            </Form.Item>
                            <Button className="auth-submit" type="primary" htmlType="submit" block loading={busy}>
                                Send code
                            </Button>
                            <div className="auth-otp-actions">
                                <Button type="link" htmlType="button" onClick={cancel} disabled={busy}>Back to sign in</Button>
                            </div>
                        </Form>
                    </>
                )}

                {phase === PHASE.FORGOT_CONFIRM && (
                    <>
                        <Brand title="Choose a new password" sub="Your authenticator app stays the same. You will sign in with this password and a code from the app." />
                        {notice && <Alert type="success" showIcon message={notice} className="auth-alert" />}
                        {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                        <Form className="auth-form" layout="vertical" requiredMark={false} onFinish={submitReset} disabled={busy}>
                            <Form.Item name="code" label="Code from the email" rules={[{ required: true, pattern: /^\d{6}$/, message: 'Enter the 6-digit code from the email.' }]}>
                                <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6-digit code" autoFocus />
                            </Form.Item>
                            <PasswordField person={{ email }} />
                            <Typography.Paragraph type="secondary">
                                Lost your phone? Ask an administrator to reset your authenticator.
                            </Typography.Paragraph>
                            <Button className="auth-submit" type="primary" htmlType="submit" block loading={busy}>
                                Set password
                            </Button>
                            <div className="auth-otp-actions">
                                <Button type="link" htmlType="button" onClick={resendCode} disabled={busy || resendWait > 0}>
                                    {resendWait > 0 ? 'Send a new code (' + resendWait + 's)' : 'Send a new code'}
                                </Button>
                                <Button type="link" htmlType="button" onClick={cancel} disabled={busy}>Back to sign in</Button>
                            </div>
                        </Form>
                    </>
                )}

                {phase === PHASE.NEW_PASSWORD && (
                    <>
                        <Brand title="Choose your password" />
                        {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                        <Typography.Paragraph className="auth-welcome-subtext">
                            Welcome. Please replace the temporary password from your invitation with one of your own.
                        </Typography.Paragraph>
                        <Form layout="vertical" requiredMark={false} onFinish={submitNewPassword} disabled={busy}>
                            <PasswordField person={{ email }} />
                            <Button type="primary" htmlType="submit" size="large" block loading={busy}>
                                Continue
                            </Button>
                            <div className="auth-otp-actions">
                                <Button type="link" onClick={cancel} disabled={busy}>Start again</Button>
                            </div>
                        </Form>
                    </>
                )}

                {phase === PHASE.MFA_SETUP && setup && (
                    <>
                        <Brand title="Set up Authenticator" />
                        <MfaSetup
                            secret={setup.secret}
                            otpauthUri={setup.otpauthUri}
                            busy={busy}
                            error={error}
                            onVerify={submitSetupCode}
                            onCancel={cancel}
                        />
                    </>
                )}

                {phase === PHASE.MFA && (
                    <>
                        <Brand title="Enter Authenticator Code" sub="Please enter the code from your authenticator app" />
                        <form
                            className="auth-otp auth-login-otp"
                            onSubmit={(event) => {
                                event.preventDefault();
                                submitMfa(mfaCode);
                            }}
                        >
                            {error && <Alert type="error" showIcon message={error} className="auth-alert" />}
                            <Input.OTP
                                className="auth-otp-input"
                                key={mfaKey}
                                length={6}
                                value={mfaCode}
                                disabled={busy}
                                onChange={(code) => {
                                    setMfaCode(code);
                                    // antd fires this only once every box is filled.
                                    submitMfa(code);
                                }}
                                onInput={(digits) => setMfaCode(digits.join(''))}
                                autoFocus
                            />
                            <div className="auth-login-otp-actions">
                                <span>Having trouble?</span>
                                <Button type="link" onClick={cancel} disabled={busy}>Use a different account</Button>
                            </div>
                            <Button className="auth-submit" type="primary" htmlType="submit" block loading={busy}>
                                Submit
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
};

export default AuthGate;
